import { IncomingMessage, ServerResponse } from 'http';

/**
 * A request type that exposes cookies. Supports both Node.js IncomingMessage
 * (where headers.cookie is a string) and Web API requests like NextRequest
 * (where headers.get('cookie') returns the cookie string).
 */
interface WebApiHeaders {
  get(name: string): string | null;
}

export type CookieReadableRequest =
  | Pick<IncomingMessage, 'headers'>
  | { headers: WebApiHeaders };

/**
 * Extracts the raw cookie header string from either an IncomingMessage or a
 * Web API request (e.g. NextRequest).
 */
export function getCookieHeader(req?: CookieReadableRequest): string | undefined {
  if (!req) return undefined;
  // NextRequest / Web API Headers (has .get method)
  const headers = req.headers as { get?(name: string): string | null };
  if (typeof headers?.get === 'function') {
    return headers.get('cookie') ?? undefined;
  }
  // IncomingMessage (has .cookie property)
  return (req as Pick<IncomingMessage, 'headers'>).headers?.cookie;
}

class Cookies {
  cookies: Record<string, string> = {};

  constructor(cookieHeader?: string) {
    cookieHeader?.split(/\s*;\s*/)?.forEach(cookie => {
      const [key, value] = cookie.split(/\s*=\s*/);
      this.cookies[key] = value;
    });
  }

  get(name: string): string | undefined {
    return this.cookies[name];
  }
}

export function getObjectFromRequestCookie<T = Record<string, string>>(name: string, req?: CookieReadableRequest): T | undefined {
  const cookieHeader = getCookieHeader(req);
  if (cookieHeader) {
    const cookies = new Cookies(cookieHeader);
    const cookie = cookies.get(name);

    if (cookie) {
      return getJSON<T>(cookie);
    }
  }
  return undefined;
}

export function getStringFromRequestCookie(name: string, req?: CookieReadableRequest, defaultValue?: string): string | undefined {
  const cookieHeader = getCookieHeader(req);
  if (cookieHeader) {
    const cookies = new Cookies(cookieHeader);
    return cookies.get(name) || defaultValue;
  }
  return defaultValue;
}

export function getJSON<T = string>(cookie?: string): T | undefined {
  if (cookie) {
    try {
      return JSON.parse(decodeURIComponent(cookie)) as T;
    } catch (e){
      console.error(e);
      return undefined;
    }
  }
  return undefined;
}

export function getResponseCookieValue(res: ServerResponse, name: string): any | undefined {
  const setCookieHeader = res.getHeader('Set-Cookie');

  if (!setCookieHeader) {
    return undefined;
  }

  const cookies = Array.isArray(setCookieHeader) ? setCookieHeader : [setCookieHeader];

  for (const cookie of cookies) {
    const cookieParts = cookie.toString().split(';')[0].split('=');

    if (cookieParts[0] !== name) {
      return undefined;
    }

    try {
      const cookieValue = cookieParts[1];

      // Decode the URL-encoded JSON string
      const decodedValue = decodeURIComponent(cookieValue);

      // Parse the JSON string
      const parsedData = JSON.parse(decodedValue);
      return parsedData;
    } catch (error) {
      console.error('Failed to parse JSON string:', error);
    }
  }

  return undefined;
}